import { apiGetCustomers } from "@lib/api/customer";
import { apiGetAIWalletAddress } from "@lib/api/walletAI";
import { WalletAddressText } from "@lib/components/MiniComponents/Others";
import CustomerName from "@lib/components/MiniComponents/Others/CustomerName";
import { StyledFlex } from "@lib/components/MiniComponents/Table/index.styled";
import { useTypedSelector } from "@lib/redux";
import { AuthRoles, CurrencyEnum } from "@lib/types";
import { UseMutationProps } from "@lib/types/Api";
import { AIBlockchainPayment, AIWalletAddress } from "@lib/types/Wallet";
import { useQuery } from "@tanstack/react-query";
import { Button, Form, Input, Modal, Select, Space, Switch } from "antd";
import { useCallback, useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useIntl } from "react-intl";

const layout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 16 },
};

type Props = {
  openModal: boolean;
  toggleModal: (status?: boolean) => void;
  addMutationQuery: UseMutationProps<any>;
  updateMutationQuery?: UseMutationProps<any>;
  customerId?: string;
  walletAddress?: AIWalletAddress;
};

export default function AIPaymentModal<T extends AIBlockchainPayment>({
  openModal,
  toggleModal,
  addMutationQuery,
  updateMutationQuery,
  customerId: customerIdProp,
  walletAddress: walletAddressProp,
}: Props) {
  const { messages } = useIntl();
  const { user } = useTypedSelector((state) => state.auth);
  const userRole = user?.role;
  const [form] = Form.useForm();
  const watchedCustomerId = Form.useWatch("customerId", form);
  const customerId = customerIdProp || watchedCustomerId;
  const canAddPaidCredit = updateMutationQuery;
  const [creditType, setCreditType] = useState<"free" | "paid">("free");

  useEffect(() => {
    if (creditType === "paid") {
      form.setFieldValue("amount", walletAddressProp?.remainingBalance);
    } else {
      form.setFieldValue("amount", 0);
    }
  }, [creditType, form, walletAddressProp]);

  const { mutate: addMutate, isPending: addIsPending } = addMutationQuery;
  const { mutate: updateMutate, isPending: updateIsPending } =
    updateMutationQuery || { mutate: () => {}, isPending: false };

  const isPending = addIsPending || updateIsPending;

  const { data: customers, isLoading: customersIsLoading } = useQuery({
    queryFn: () => apiGetCustomers({ limit: 100 }),
    queryKey: ["customers-dropdown"],
    enabled: openModal && userRole === AuthRoles.Admin,
  });

  const { data: walletAddressData, isLoading: walletAddressIsLoading } =
    useQuery({
      queryFn: () =>
        apiGetAIWalletAddress({
          limit: 100,
          filter: [`customerId||$eq||${customerId}`],
        }),
      queryKey: ["ai-wallet-address-dropdown", { customerId }],
      enabled: openModal && !!customerId,
    });

  const handleResetForm = useCallback(() => {
    form?.resetFields();
  }, []);

  const handleClose = useCallback(() => {
    handleResetForm();
    toggleModal(false);
  }, [toggleModal, form]);

  useEffect(() => {
    if (openModal) {
      handleResetForm();
    }
  }, [openModal]);

  useEffect(() => {
    if (!openModal) return;
    form.setFieldValue("walletAddressId", walletAddressProp?.id);
  }, [walletAddressProp, form, walletAddressData]);

  return (
    <Modal
      title={
        <FormattedMessage
          id={"common.createTitle"}
          values={{
            title: messages["common.credit"] as string,
          }}
        />
      }
      open={openModal}
      onCancel={handleClose}
      footer={null}
      confirmLoading={isPending}
    >
      <Form<T>
        {...layout}
        form={form}
        name={"sidebar.freeCredit-modal"}
        onFinish={(values) => {
          if (creditType === "paid") {
            updateMutate({
              id: values.walletAddressId,
              remainingBalance: Number(values.amount),
            });
          } else {
            addMutate({ id: values.walletAddressId, amount: values.amount });
          }
        }}
        style={{ maxWidth: 600, marginTop: 20 }}
        autoComplete="off"
        initialValues={{
          customerId,
          walletAddressId: null,
          amount: 0,
        }}
      >
        {userRole === AuthRoles.Admin && (
          <Form.Item
            name="customerId"
            label={messages["common.customer"] as string}
            rules={[{ required: true }]}
          >
            {customerIdProp ? (
              <>
                <Input type="hidden" />
                <CustomerName
                  customer={customers?.data?.data?.find(
                    (customer) => customer.id === customerIdProp,
                  )}
                />
              </>
            ) : (
              <Select
                showSearch
                loading={customersIsLoading}
                optionFilterProp="text"
                placeholder={messages["common.select"] as string}
                options={customers?.data?.data?.map((customer) => ({
                  label: <CustomerName customer={customer} />,
                  text: customer.name,
                  value: customer.id,
                }))}
              />
            )}
          </Form.Item>
        )}

        <Form.Item
          name="walletAddressId"
          label={messages["common.walletAddress"] as string}
          rules={[{ required: true }]}
        >
          <Select
            showSearch
            loading={walletAddressIsLoading}
            optionFilterProp="text"
            options={[
              ...(walletAddressData?.data?.data?.map((item) => ({
                label: (
                  <WalletAddressText
                    walletAddress={item}
                    showCopyButton={false}
                    shortenAddress={20}
                  />
                ),
                text: item?.address,
                value: item?.id,
              })) || []),
            ]}
            disabled={!customerId}
            placeholder={
              <FormattedMessage
                id={"helper.pleaseSelectToX"}
                values={{ x: messages["common.walletAddress"] as string }}
              />
            }
          />
        </Form.Item>

        <Form.Item
          name="creditType"
          label={messages["common.creditType"] as string}
          help={messages[`common.creditType.${creditType}.help`] as string}
        >
          <Switch
            disabled={!canAddPaidCredit}
            checked={creditType === "free"}
            onChange={(checked) => setCreditType(checked ? "free" : "paid")}
            checkedChildren={messages["common.creditType.free"] as string}
            unCheckedChildren={messages["common.creditType.paid"] as string}
          />
        </Form.Item>

        <Form.Item
          name="amount"
          label={messages["common.amount"] as string}
          rules={[{ required: true }]}
        >
          <Input
            type="number"
            placeholder={messages["common.amount"] as string}
            addonAfter={
              <Select
                style={{ width: "100px" }}
                value={CurrencyEnum.USD}
                disabled
                options={[
                  {
                    label: CurrencyEnum.USD,
                    value: CurrencyEnum.USD,
                  },
                ]}
              />
            }
          />
        </Form.Item>

        <StyledFlex style={{ justifyContent: "end" }}>
          <Space>
            <Button
              htmlType="button"
              onClick={handleClose}
              disabled={isPending}
            >
              {messages["common.reset"] as string}
            </Button>
            <Button type="primary" htmlType="submit" loading={isPending}>
              {messages["common.create"] as string}
            </Button>
          </Space>
        </StyledFlex>
      </Form>
    </Modal>
  );
}
